<template>
  <div class="shell">
    <header class="topbar">
      <div class="topbar__inner">
        <router-link to="/" class="brand">
          <img class="brand__mark" :src="zarokaaMark" alt="" width="23" height="28" />
          <span class="brand__text">
            {{ t("brand.name") }}<span class="brand__suffix">{{ t("brand.suffix") }}</span>
          </span>
        </router-link>

        <nav class="topbar__nav" :aria-label="t('nav.build')">
          <template v-if="session.token">
            <router-link to="/apps">{{ t("nav.apps") }}</router-link>
            <router-link to="/notices">{{ t("nav.notices") }}</router-link>
            <router-link to="/team">{{ t("nav.team") }}</router-link>
            <router-link to="/webhooks">{{ t("nav.webhooks") }}</router-link>
          </template>
          <router-link to="/docs">{{ t("nav.docs") }}</router-link>
          <router-link to="/docs/reference">{{ t("nav.reference") }}</router-link>
          <router-link to="/console">{{ t("nav.console") }}</router-link>
        </nav>

        <div class="topbar__end">
          <LocalePicker />
          <template v-if="session.token">
            <div class="account">
              <button
                type="button"
                class="account__trigger"
                :aria-expanded="menuOpen"
                @click="menuOpen = !menuOpen"
              >
                <span class="avatar" aria-hidden="true">{{ initials }}</span>
                <span class="account__name">{{ displayName }}</span>
              </button>
              <div v-if="menuOpen" class="account__menu">
                <div class="account__id">
                  <div class="strong small truncate">{{ session.user?.email }}</div>
                  <div class="xs muted truncate">{{ session.developer?.name }}</div>
                </div>
                <router-link to="/provider" class="account__item" @click="menuOpen = false">
                  {{ t("nav.provider") }}
                </router-link>
                <router-link to="/security" class="account__item" @click="menuOpen = false">
                  {{ t("nav.security") }}
                </router-link>
                <button type="button" class="account__item" @click="onSignOut">
                  {{ t("nav.signOut") }}
                </button>
              </div>
            </div>
          </template>
          <template v-else>
            <router-link to="/sign-in" class="btn btn--secondary btn--sm">
              {{ t("nav.signIn") }}
            </router-link>
            <router-link to="/sign-up" class="btn btn--sm">{{ t("nav.signUp") }}</router-link>
          </template>
        </div>
      </div>
    </header>

    <div v-if="providerBlocked" class="strip strip--danger">
      <div class="strip__inner">{{ t("banner.providerBlocked") }}</div>
    </div>
    <div v-else-if="providerPending" class="strip strip--info">
      <div class="strip__inner">{{ t("banner.providerPending") }}</div>
    </div>
    <div v-else-if="needsTotp" class="strip strip--warn">
      <div class="strip__inner">
        {{ t("banner.totpMissing") }}
        <router-link to="/security">{{ t("banner.totpAction") }}</router-link>
      </div>
    </div>

    <main class="shell__main" :class="{ 'shell__main--wide': wide }">
      <router-view />
    </main>

    <footer class="footer">
      <div class="footer__inner">
        <span class="muted small">{{ t("brand.name") }} · {{ t("brand.suffix") }}</span>
        <nav class="footer__links small">
          <router-link to="/docs/guides">{{ t("nav.guides") }}</router-link>
          <router-link to="/docs/reference">{{ t("nav.reference") }}</router-link>
          <router-link to="/docs/changelog">{{ t("nav.changelog") }}</router-link>
        </nav>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import zarokaaMark from "./assets/zarokaa-mark.svg";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { session, signOut } from "./lib/session";
import LocalePicker from "./components/LocalePicker.vue";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const menuOpen = ref(false);

/**
 * The API reference needs the full window width; every other screen reads
 * better in a measured column. Driven by route meta rather than by a name list
 * so a new wide screen declares itself.
 */
const wide = computed(() => !!route.meta.wide);

const signedIn = computed(() => !!session.token && !!session.user);
const providerBlocked = computed(() => signedIn.value && session.developer?.status === "blocked");
const providerPending = computed(
  () => signedIn.value && ["pending", "invited"].includes(session.developer?.status)
);
const needsTotp = computed(
  () => signedIn.value && !!session.developer && !session.totpEnabled && route.name !== "security"
);

const displayName = computed(
  () => session.developer?.name || session.user?.email || ""
);

const initials = computed(() => {
  const source = session.developer?.name || session.user?.email || "?";
  return source
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
});

// A menu left open across a navigation hangs over the new screen.
watch(route, () => {
  menuOpen.value = false;
});

async function onSignOut() {
  menuOpen.value = false;
  await signOut();
  router.push({ name: "sign-in" });
}
</script>

<style scoped>
.shell { min-height: 100dvh; display: flex; flex-direction: column; }

/**
 * The reference route is FULL WIDTH but scrolls with the page.
 *
 * It used to be locked to the viewport with the document rendered inside a
 * fixed-height panel — which clipped 21,000px of reference into a 350px box
 * with `overflow: hidden` and no way to reach any of it. Scalar is built to
 * scroll the window and stick its own sidebar; bounding it fought that and won
 * nothing. The page scrolls, the sidebar sticks, the sticky top bar stays.
 */

/* --- top bar ----------------------------------------------------------- */
.topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(255, 255, 255, .88);
  /* The blur is what keeps a long reference page readable as it scrolls under
     the bar; the solid fallback below covers browsers without it. */
  backdrop-filter: saturate(180%) blur(12px);
  border-bottom: 1px solid var(--line);
}
@supports not (backdrop-filter: blur(1px)) {
  .topbar { background: var(--bg-raised); }
}

.topbar__inner {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  height: var(--header-h);
  max-width: var(--content-max);
  margin: 0 auto;
  padding: 0 var(--space-5);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--ink-strong);
  font-weight: 600;
  letter-spacing: -0.01em;
  text-decoration: none;
  white-space: nowrap;
}
.brand:hover { text-decoration: none; }
.brand__mark { display: block; width: 23px; height: 28px; }
.brand__suffix { color: var(--ink-muted); font-weight: 400; margin-left: .4ch; }

.topbar__nav {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}
.topbar__nav::-webkit-scrollbar { display: none; }

.topbar__nav a {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius);
  color: var(--ink-muted);
  font-size: var(--text-sm);
  font-weight: 500;
  white-space: nowrap;
  transition: background var(--transition), color var(--transition);
}
.topbar__nav a:hover { background: var(--bg-sunken); color: var(--ink-strong); text-decoration: none; }
.topbar__nav a.router-link-active { color: var(--accent); background: var(--accent-soft); }

.topbar__end { display: flex; align-items: center; gap: var(--space-2); }

/* --- account ----------------------------------------------------------- */
.account { position: relative; }
.account__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 34px;
  padding: 0 var(--space-2);
  border: 0;
  border-radius: var(--radius);
  background: transparent;
  font: inherit;
  font-size: var(--text-sm);
  color: var(--ink);
  cursor: pointer;
}
.account__trigger:hover { background: var(--bg-sunken); }
.account__name { max-width: 12ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.avatar {
  display: grid;
  place-items: center;
  width: 26px; height: 26px;
  border-radius: 50%;
  background: var(--brand-100);
  color: var(--brand-700);
  font-size: 11px;
  font-weight: 600;
}

.account__menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  min-width: 220px;
  padding: var(--space-1);
  background: var(--bg-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
}
.account__id { padding: var(--space-3); border-bottom: 1px solid var(--line); margin-bottom: var(--space-1); }
.account__item {
  display: block;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--ink);
  font: inherit;
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
}
.account__item:hover { background: var(--bg-sunken); text-decoration: none; }

/* --- strips ------------------------------------------------------------ */
.strip { font-size: var(--text-sm); }
.strip__inner { max-width: var(--content-max); margin: 0 auto; padding: var(--space-3) var(--space-5); }
.strip--warn { background: var(--amber-50); color: var(--amber-700); }
.strip--info { background: var(--blue-50); color: var(--blue-700); }
.strip--danger { background: var(--red-50); color: var(--red-700); }
.strip a { color: inherit; text-decoration: underline; margin-left: .5ch; }

/* --- main / footer ----------------------------------------------------- */
.shell__main {
  flex: 1 1 auto;
  width: 100%;
  max-width: var(--content-max);
  margin: 0 auto;
  padding: var(--space-6) var(--space-5) var(--space-8);
}
.shell__main--wide {
  max-width: none;
  padding: var(--space-5) var(--space-4) var(--space-4);
}

.footer { border-top: 1px solid var(--line); background: var(--bg-raised); }
.footer__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
  max-width: var(--content-max);
  margin: 0 auto;
  padding: var(--space-5);
}
.footer__links { display: flex; gap: var(--space-4); }
.footer__links a { color: var(--ink-muted); }

@media (max-width: 860px) {
  .topbar__inner { gap: var(--space-3); padding: 0 var(--space-4); }
  .account__name { display: none; }
  .shell__main { padding: var(--space-5) var(--space-4) var(--space-7); }
}
</style>
